import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, CheckCircle, Phone, ArrowRight, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Large, tap-friendly rating row (min 32px star, dark outline when unselected). */
function StarRow({ value, onChange, label, required, testId }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="mb-6" data-testid={testId}>
      <label className="block text-sm font-semibold text-[#1A2421] mb-3 leading-snug">
        {label}{required && <span className="text-[#C45A41] ml-1">*</span>}
      </label>
      <div className="flex gap-2.5">
        {[1, 2, 3, 4, 5].map(n => {
          const active = (hover || value || 0) >= n;
          return (
            <button key={n} type="button"
              onClick={() => onChange(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              aria-label={`${label} ${n} out of 5`}
              className={`flex-1 aspect-square min-h-[52px] rounded-2xl flex items-center justify-center transition-all duration-150 active:scale-95 ${
                active
                  ? 'bg-gradient-to-b from-amber-400 to-amber-500 text-white border-2 border-amber-500 shadow-md shadow-amber-500/40 scale-105'
                  : 'bg-white text-[#1A2421]/50 border-2 border-[#1A2421]/25 hover:border-amber-400 hover:text-amber-500'
              }`}
              data-testid={`${testId}-star-${n}`}>
              <Star className="w-8 h-8" fill={active ? 'currentColor' : 'none'} strokeWidth={2.25} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Yes/No control */
function YesNoRow({ value, onChange, label, required, testId }) {
  return (
    <div className="mb-6" data-testid={testId}>
      <label className="block text-sm font-semibold text-[#1A2421] mb-3 leading-snug">
        {label}{required && <span className="text-[#C45A41] ml-1">*</span>}
      </label>
      <div className="flex gap-2.5">
        {['yes', 'no'].map(opt => {
          const active = value === opt;
          return (
            <button key={opt} type="button"
              onClick={() => onChange(opt)}
              className={`flex-1 h-12 rounded-xl font-semibold text-sm capitalize transition-all active:scale-[0.98] border-2 ${
                active
                  ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)] shadow-md shadow-[var(--color-primary)]/20'
                  : 'bg-white text-[#1A2421] border-[#1A2421]/20 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]'
              }`}
              data-testid={`${testId}-${opt}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PublicFeedback() {
  const { outletId, formType } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [logoOk, setLogoOk] = useState(true);
  const [phone, setPhone] = useState('');
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [serverError, setServerError] = useState('');
  const [form, setForm] = useState({
    overall_rating: 0,
    food_rating: 0,
    service_rating: 0,
    ambience_rating: 0,
    packaging_rating: 0,
    delivery_rating: 0,
    comments: '',
    name: '',
  });
  const [customAnswers, setCustomAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isDelivery = formType === 'delivery';
  const fc = landing?.form_config || {};
  const customQuestions = useMemo(
    () => (landing?.custom_questions || []).filter(q => q && q.question),
    [landing],
  );

  useEffect(() => {
    axios.get(`${API}/feedback/landing/${outletId}`)
      .then(r => setLanding(r.data))
      .catch(() => setLanding({ _missing: true }));
    if (!isDelivery) setPhoneLocked(true);
  }, [outletId, isDelivery]);

  const validatePhone = () => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10 || !/^[6-9]/.test(cleaned)) {
      setPhoneError('Enter a valid 10-digit Indian mobile (starts with 6–9)');
      return false;
    }
    setPhoneError('');
    setPhone(cleaned);
    return true;
  };

  const handlePhoneContinue = () => {
    if (!validatePhone()) return;
    setPhoneLocked(true);
  };

  const visibleFields = () => {
    if (isDelivery) {
      return {
        food: fc.food?.enabled !== false,
        packaging: fc.packaging?.enabled !== false,
        delivery: fc.delivery?.enabled !== false,
      };
    }
    return {
      food: fc.food?.enabled !== false,
      service: fc.service?.enabled !== false,
      ambience: fc.ambience?.enabled !== false,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!form.overall_rating) {
      setServerError('Please give an overall rating');
      return;
    }
    if (isDelivery && !validatePhone()) return;
    const v = visibleFields();
    const required = (key) => v[key] && fc[key]?.required;
    if (required('food') && !form.food_rating) return setServerError(`Please rate ${fc.food?.label || 'Food'}`);
    if (required('service') && !form.service_rating) return setServerError(`Please rate ${fc.service?.label || 'Service'}`);
    if (required('ambience') && !form.ambience_rating) return setServerError(`Please rate ${fc.ambience?.label || 'Ambience'}`);
    if (required('packaging') && !form.packaging_rating) return setServerError(`Please rate ${fc.packaging?.label || 'Packaging'}`);
    if (required('delivery') && !form.delivery_rating) return setServerError(`Please rate ${fc.delivery?.label || 'Delivery'}`);

    // Validate custom required questions
    for (const q of customQuestions) {
      if (q.required) {
        const a = customAnswers[q.id];
        const missing = a === undefined || a === null || a === '' || a === 0;
        if (missing) return setServerError(`Please answer: ${q.question}`);
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        outlet_id: outletId,
        form_type: formType,
        overall_rating: form.overall_rating,
        comments: form.comments,
        name: form.name,
        phone: phone || undefined,
      };
      if (v.food) payload.food_rating = form.food_rating || null;
      if (isDelivery) {
        if (v.packaging) payload.packaging_rating = form.packaging_rating || null;
        if (v.delivery) payload.delivery_rating = form.delivery_rating || null;
      } else {
        if (v.service) payload.service_rating = form.service_rating || null;
        if (v.ambience) payload.ambience_rating = form.ambience_rating || null;
      }
      // Custom responses
      if (customQuestions.length) {
        payload.custom_responses = customQuestions
          .map(q => ({
            question_id: q.id,
            question: q.question,
            type: q.type,
            answer: customAnswers[q.id] ?? '',
          }))
          .filter(r => r.answer !== '' && r.answer !== 0 && r.answer !== null && r.answer !== undefined);
      }
      await axios.post(`${API}/feedback`, payload);
      navigate(`/feedback/${outletId}/thanks`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setServerError(detail || 'You have already submitted feedback today. Thank you!');
      } else {
        setServerError(detail || 'Submission failed — please try again');
      }
      setSubmitting(false);
    }
  };

  if (!landing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#EEF6F0] via-[#F5F2EA] to-[#F9F9F4]">
        <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (landing._missing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#EEF6F0] via-[#F5F2EA] to-[#F9F9F4] p-6 text-center">
        <div>
          <p className="text-lg font-semibold text-[#1A2421]">Outlet not found</p>
          <p className="text-sm text-[#5C6B64] mt-2">Please check the QR code and try again.</p>
        </div>
      </div>
    );
  }

  // Outlet logo ONLY — if missing or failed, show outlet name initial as hero (no platform logo).
  const showLogo = landing.has_logo && logoOk;
  const outletInitial = (landing.outlet_name || '?').trim().charAt(0).toUpperCase();

  const LogoBlock = (
    <div className="w-[88px] h-[88px] bg-white rounded-2xl mx-auto mb-3 flex items-center justify-center p-2 shadow-[0_2px_12px_rgba(20,61,48,0.08)] border border-[#E2E8DF] overflow-hidden">
      {showLogo ? (
        <img
          src={`${API}/outlets/${outletId}/logo`}
          alt={landing.outlet_name}
          onError={() => setLogoOk(false)}
          className="max-w-full max-h-full object-contain"
          data-testid="outlet-logo"
        />
      ) : (
        <span
          className="text-3xl font-bold text-[color:var(--color-primary)] tracking-tight"
          data-testid="outlet-logo-fallback"
          aria-label={landing.outlet_name}
        >
          {outletInitial}
        </span>
      )}
    </div>
  );

  // ── Delivery phone gate ──
  if (isDelivery && !phoneLocked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EEF6F0] via-[#F5F2EA] to-[#F9F9F4] px-4 py-8 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-[420px] mx-auto">
          <div className="w-full bg-white rounded-2xl shadow-[0_10px_40px_rgba(20,61,48,0.12)] border border-white/60 p-6">
            <div className="text-center mb-6">
              {LogoBlock}
              <h1 className="text-[22px] font-bold text-[#1A2421] tracking-tight" data-testid="outlet-name">{landing.outlet_name}</h1>
              <p className="text-[11px] font-semibold text-[color:var(--color-primary)] uppercase tracking-[0.18em] mt-1.5">Delivery feedback</p>
            </div>
            <div className="text-center mb-6 px-2">
              <Phone className="w-10 h-10 text-[color:var(--color-primary)] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[15px] font-semibold text-[#1A2421]">Share your contact number</p>
              <p className="text-[13px] text-[#5C6B64] mt-2 leading-relaxed">So we can verify your order and reach out if needed.</p>
            </div>
            <div className="mb-5">
              <div className="flex gap-2">
                <div className="h-14 px-4 flex items-center rounded-xl bg-[#F3F5F2] text-sm font-semibold text-[#1A2421] border border-[#E2E8DF]">+91</div>
                <input type="tel" inputMode="numeric" value={phone}
                  onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setPhoneError(''); }}
                  placeholder="10-digit mobile" maxLength={10}
                  data-testid="phone-input"
                  className="flex-1 min-w-0 h-14 px-4 text-base tabular-nums font-semibold bg-white border-2 border-[#1A2421]/15 rounded-xl placeholder:text-[#5C6B64]/70 focus:border-[color:var(--color-primary)] focus:outline-none transition-colors" />
              </div>
              {phoneError && (
                <p className="text-xs text-[#C45A41] mt-2.5 flex items-center gap-1.5 font-medium" data-testid="phone-error">
                  <AlertCircle className="w-3.5 h-3.5" />{phoneError}
                </p>
              )}
            </div>
            <button type="button" onClick={handlePhoneContinue} disabled={!phone}
              data-testid="phone-continue-btn"
              className="w-full h-14 bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white rounded-xl font-bold text-base disabled:opacity-40 flex items-center justify-center gap-2 transition-colors active:scale-[0.98] shadow-lg shadow-[var(--color-primary)]/20">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main feedback form ──
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EEF6F0] via-[#F5F2EA] to-[#F9F9F4] overflow-x-hidden">
      <div className="w-full max-w-[420px] mx-auto px-4 pt-8 pb-[120px]">
        <div className="text-center mb-6">
          {LogoBlock}
          <h1 className="text-[22px] font-bold text-[#1A2421] tracking-tight" data-testid="outlet-name">{landing.outlet_name}</h1>
          <p className="text-[11px] font-semibold text-[color:var(--color-primary)] uppercase tracking-[0.18em] mt-1.5" data-testid="form-type-label">
            {isDelivery ? 'Delivery feedback' : 'Dine-in feedback'}
          </p>
          <p className="text-[14px] text-[#5C6B64] mt-3">We'd love your feedback</p>
        </div>

        <form onSubmit={handleSubmit}
          className="bg-white rounded-2xl p-6 shadow-[0_10px_40px_rgba(20,61,48,0.10)] border border-white/60"
          data-testid="feedback-form">
          <StarRow label="Overall experience" required value={form.overall_rating}
            onChange={v => setForm(f => ({ ...f, overall_rating: v }))}
            testId="overall-rating" />

          {(fc.food?.enabled !== false) && (
            <StarRow label={fc.food?.label || 'Food'} required={!!fc.food?.required}
              value={form.food_rating}
              onChange={v => setForm(f => ({ ...f, food_rating: v }))}
              testId="food-rating" />
          )}

          {isDelivery ? (
            <>
              {(fc.packaging?.enabled !== false) && (
                <StarRow label={fc.packaging?.label || 'Packaging'} required={!!fc.packaging?.required}
                  value={form.packaging_rating}
                  onChange={v => setForm(f => ({ ...f, packaging_rating: v }))}
                  testId="packaging-rating" />
              )}
              {(fc.delivery?.enabled !== false) && (
                <StarRow label={fc.delivery?.label || 'Delivery experience'} required={!!fc.delivery?.required}
                  value={form.delivery_rating}
                  onChange={v => setForm(f => ({ ...f, delivery_rating: v }))}
                  testId="delivery-rating" />
              )}
            </>
          ) : (
            <>
              {(fc.service?.enabled !== false) && (
                <StarRow label={fc.service?.label || 'Service'} required={!!fc.service?.required}
                  value={form.service_rating}
                  onChange={v => setForm(f => ({ ...f, service_rating: v }))}
                  testId="service-rating" />
              )}
              {(fc.ambience?.enabled !== false) && (
                <StarRow label={fc.ambience?.label || 'Ambience'} required={!!fc.ambience?.required}
                  value={form.ambience_rating}
                  onChange={v => setForm(f => ({ ...f, ambience_rating: v }))}
                  testId="ambience-rating" />
              )}
            </>
          )}

          {/* Custom questions */}
          {customQuestions.length > 0 && (
            <>
              <div className="h-px bg-[#E2E8DF] my-6" />
              {customQuestions.map(q => {
                if (q.type === 'rating') {
                  return (
                    <StarRow key={q.id} label={q.question} required={!!q.required}
                      value={customAnswers[q.id] || 0}
                      onChange={v => setCustomAnswers(a => ({ ...a, [q.id]: v }))}
                      testId={`custom-${q.id}`} />
                  );
                }
                if (q.type === 'yesno') {
                  return (
                    <YesNoRow key={q.id} label={q.question} required={!!q.required}
                      value={customAnswers[q.id] || ''}
                      onChange={v => setCustomAnswers(a => ({ ...a, [q.id]: v }))}
                      testId={`custom-${q.id}`} />
                  );
                }
                return (
                  <div key={q.id} className="mb-6" data-testid={`custom-${q.id}`}>
                    <label className="block text-sm font-semibold text-[#1A2421] mb-3 leading-snug">
                      {q.question}{q.required && <span className="text-[#C45A41] ml-1">*</span>}
                    </label>
                    <input type="text" value={customAnswers[q.id] || ''}
                      onChange={e => setCustomAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                      maxLength={300} placeholder="Your answer…"
                      data-testid={`custom-${q.id}-input`}
                      className="w-full h-12 px-4 text-sm bg-white border-2 border-[#1A2421]/15 rounded-xl placeholder:text-[#5C6B64]/70 focus:border-[color:var(--color-primary)] focus:outline-none transition-colors" />
                  </div>
                );
              })}
            </>
          )}

          <div className="h-px bg-[#E2E8DF] my-6" />

          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#1A2421] mb-3">
              Anything to share? <span className="text-[#5C6B64] font-normal">(optional)</span>
            </label>
            <textarea value={form.comments}
              onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
              rows={3} maxLength={500}
              placeholder="Tell us what you loved or what we can improve…"
              data-testid="comments-input"
              className="w-full px-4 py-3 text-sm bg-white border-2 border-[#1A2421]/15 rounded-xl placeholder:text-[#5C6B64]/70 focus:border-[color:var(--color-primary)] focus:outline-none resize-none transition-colors" />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-[#1A2421] mb-2">Your name <span className="text-[#5C6B64] font-normal">(opt)</span></label>
              <input type="text" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="name-input" maxLength={50}
                placeholder="Optional"
                className="w-full h-11 px-3 text-sm bg-white border-2 border-[#1A2421]/15 rounded-xl placeholder:text-[#5C6B64]/70 focus:border-[color:var(--color-primary)] focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1A2421] mb-2">
                Phone {isDelivery ? '' : <span className="text-[#5C6B64] font-normal">(opt)</span>}
              </label>
              <input type="tel" inputMode="numeric" value={phone} disabled={isDelivery}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10}
                placeholder="10-digit mobile"
                data-testid="phone-input"
                className="w-full h-11 px-3 text-sm tabular-nums bg-white border-2 border-[#1A2421]/15 rounded-xl placeholder:text-[#5C6B64]/70 focus:border-[color:var(--color-primary)] focus:outline-none transition-colors disabled:bg-[#E2E8DF] disabled:text-[#5C6B64] disabled:cursor-not-allowed" />
            </div>
          </div>

          {serverError && (
            <div className="mt-4 p-3 rounded-xl bg-[#FEF2EE] border border-[#C45A41]/40 flex items-start gap-2" data-testid="server-error">
              <AlertCircle className="w-4 h-4 text-[#C45A41] shrink-0 mt-0.5" />
              <p className="text-xs text-[#C45A41] font-medium">{serverError}</p>
            </div>
          )}

          <p className="text-[11px] text-[#5C6B64] text-center mt-5 flex items-center justify-center gap-1.5">
            <CheckCircle className="w-3 h-3" /> Confidential — shared only with the {landing.outlet_name} team.
          </p>
        </form>
      </div>

      {/* Sticky submit CTA — always visible on mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#E2E8DF] px-4 py-3 shadow-[0_-4px_20px_rgba(20,61,48,0.08)] z-50">
        <div className="w-full max-w-[420px] mx-auto">
          <button type="button"
            onClick={handleSubmit}
            disabled={submitting || !form.overall_rating}
            data-testid="submit-btn"
            className="w-full h-14 bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white rounded-xl font-bold text-[15px] tracking-wide shadow-lg shadow-[var(--color-primary)]/25 disabled:opacity-40 transition-all active:scale-[0.98]">
            {submitting ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}
